import dayjs, { Dayjs } from 'dayjs'
import { Clearable } from './Clearable'
import type { Serializable } from './Serializable'

export class DateTimeRange extends Clearable implements Serializable {
  constructor(
    public from: Dayjs | null = null,
    public to: Dayjs | null = null,
  ) {
    super()
  }

  clear(): void {
    this.from = null
    this.to = null
  }

  clearFrom(): void {
    this.from = null
  }

  clearTo(): void {
    this.to = null
  }

  isEmpty(): boolean {
    return this.from === null && this.to === null
  }

  isPartiallyEmpty(): boolean {
    return this.from === null || this.to === null
  }

  isValid(): boolean {
    if (!this.from || !this.to) {
      return false
    }

    if (!this.from.isValid() || !this.to.isValid()) {
      return false
    }

    return this.from.isBefore(this.to)
  }

  overlaps(other: DateTimeRange): boolean {
    if (this.isEmpty() || other.isEmpty()) {
      return false
    }

    const thisStart = this.from ? this.from.valueOf() : -Infinity
    const thisEnd = this.to ? this.to.valueOf() : Infinity

    const otherStart = other.from ? other.from.valueOf() : -Infinity
    const otherEnd = other.to ? other.to.valueOf() : Infinity

    return thisStart < otherEnd && thisEnd > otherStart
  }

  rangeFormatted(format: string): string {
    const fromStr = this.from ? this.from.format(format) : 'N/A'
    const toStr = this.to ? this.to.format(format) : 'N/A'

    return `${fromStr} - ${toStr}`
  }

  serialize(): string {
    const from = this.from ? this.from.toLocalDateTimeString() : ''
    const to = this.to ? this.to.toLocalDateTimeString() : ''

    return `${from}..${to}`
  }

  deserialize(serialized: string): this {
    const [from, to] = serialized.split('..')

    this.from = from ? dayjs.parseDateTime(from) : null
    this.to = to ? dayjs.parseDateTime(to) : null

    return this
  }

  clone(): DateTimeRange {
    return new DateTimeRange(this.from ? this.from.clone() : null, this.to ? this.to.clone() : null)
  }

  copyFrom(other: DateTimeRange): void {
    this.from = other.from ? other.from.clone() : null
    this.to = other.to ? other.to.clone() : null
  }
}
